import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from './firebase';
import './App.css';

const WaitlistForm = ({ className = "", buttonText = "Join the waitlist", email, setEmail, isSubmitting, isSubmitted, error, handleSubmit, source = "landing-page" }) => {
  if (isSubmitted) {
    return (
      <div className={`waitlist-form ${className}`}>
        <div className="success-message">
          <span className="checkmark">✓</span>
          <p>You're on the list! We'll be in touch soon.</p>
        </div>
      </div>
    );
  }

  const onSubmit = (e) => {
    handleSubmit(e, source);
  };

  return (
    <form className={`waitlist-form ${className}`} onSubmit={onSubmit}>
      <div className="form-group">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="email-input"
          disabled={isSubmitting}
        />
        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Joining...' : buttonText}
        </button>
      </div>
      {error && <p className="error-message">{error}</p>}
    </form>
  );
};

function App() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSubmit = async (e, source) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await addDoc(collection(db, 'waitlist'), {
        email: email,
        timestamp: new Date(),
        source: source
      });
      setIsSubmitted(true);
      setEmail('');
    } catch (err) {
      console.error('Error adding email to waitlist:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMenu = () => {
    console.log('Hamburger clicked, menu state:', !isMenuOpen);
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">
            <h1 
              onClick={() => {
                window.scrollTo({ 
                  top: 0, 
                  behavior: 'smooth' 
                });
              }}
              style={{ cursor: 'pointer' }}
            >
              TERA
            </h1>
          </div>
          <div className="header-right">
            <nav className="nav">
              <a href="#features" className="nav-link">Features</a>
              <a href="#how-it-works" className="nav-link">How it works</a>
              <a href="#pricing" className="nav-link">Pricing</a>
            </nav>
            <button 
              className="nav-cta"
              onClick={() => {
                document.querySelector('.cta').scrollIntoView({ 
                  behavior: 'smooth' 
                });
              }}
            >
              Join waitlist
            </button>
            <button 
              className="hamburger" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleMenu();
              }}
              type="button"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
          <a href="#features" className="mobile-nav-link" onClick={closeMenu}>Features</a>
          <a href="#how-it-works" className="mobile-nav-link" onClick={closeMenu}>How it works</a>
          <a href="#pricing" className="mobile-nav-link" onClick={closeMenu}>Pricing</a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <p className="hero-intro">Meet Tera, your AI backoffice voice agent</p>
              <h1 className="hero-title">
                Turn drive time
                <br />
                into <span className="gradient-text">done time.</span>
              </h1>
              <p className="hero-subtitle">
                Tap the call button, tell Tera what you need, and watch your tasks, meetings, clients, and payments take care of themselves.
                <br />
                <br />
                <strong>Arrive home with zero admin left.</strong>
              </p>
              <WaitlistForm 
                className="hero-form" 
                email={email} 
                setEmail={setEmail} 
                isSubmitting={isSubmitting} 
                isSubmitted={isSubmitted} 
                error={error} 
                handleSubmit={handleSubmit}
                source="hero-section"
              />
            </div>
            <div className="hero-image">
              <img 
                src="/app-screenshot.png" 
                alt="Tera app screenshot showing voice-controlled task management" 
                className="app-screenshot"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-divider">⸻</div>
          <h2>Why freelancers love Tera</h2>
          <div className="features-grid">
            <div className="feature">
              <h3>Get your evenings back</h3>
              <p>No need to open the laptop at night. The day's admin already happened while waiting in traffic.</p>
            </div>
            <div className="feature">
              <h3>Talk, don't click and type</h3>
              <p>Turn thoughts into tracked tasks or calendar events the moment you say them.</p>
            </div>
            <div className="feature">
              <h3>Collect payments instantly</h3>
              <p>Send price quotes, payment links and invoices before you even get home.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="demo">
        <div className="container">
          <div className="section-divider">⸻</div>
          <h2>See Tera in action</h2>
          <div className="demo-table">
            <div className="demo-header">
              <div>You say while driving</div>
              <div>Tera does it for you</div>
            </div>
            <div className="demo-row">
              <div className="demo-input">"Add a new task: Send revised quote to Project Horizon, due next Tuesday."</div>
              <div className="demo-output">Task created, tagged, and scheduled.</div>
            </div>
            <div className="demo-row">
              <div className="demo-input">"Move tomorrow's kickoff to Friday at 10, mark confirmed."</div>
              <div className="demo-output">Calendar updated, invite sent.</div>
            </div>
            <div className="demo-row">
              <div className="demo-input">"Request $2 500 from Hanna Martin for May support, email it now."</div>
              <div className="demo-output">Payment link generated and emailed.</div>
            </div>
            <div className="demo-row">
              <div className="demo-input">"Create client BlueWave Ltd., contact John Lee."</div>
              <div className="demo-output">CRM entry added with all details.</div>
            </div>
            <div className="demo-row">
              <div className="demo-input">"Tera, daily brief."</div>
              <div className="demo-output">Spoken summary of today's tasks, meetings, and unpaid invoices.</div>
            </div>
          </div>
          <p className="demo-tagline"><span className="static-gradient-text">No screens, no typing - just speak and keep driving.</span></p>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="capabilities">
        <div className="container">
          <div className="section-divider">⸻</div>
          <h2>What can Tera do for you?</h2>
          <div className="capabilities-grid">
            <div className="capability">
              <h4>Task management</h4>
              <p>Capture, complete, or delete tasks; tag them to any project or client.</p>
            </div>
            <div className="capability">
              <h4>Calendar control</h4>
              <p>Book, reschedule, confirm, or ask "What's next?".</p>
            </div>
            <div className="capability">
              <h4>Client & project CRM</h4>
              <p>Create or update records on the fly.</p>
            </div>
            <div className="capability">
              <h4>Payment & invoices</h4>
              <p>Generate links, send invoices, track what's paid.</p>
            </div>
            <div className="capability">
              <h4>Email follow-up</h4>
              <p>Draft and send messages tied to any task or payment.</p>
            </div>
            <div className="capability">
              <h4>Daily brief</h4>
              <p>One voice command gives you the full picture of your day.</p>
            </div>
          </div>
          <p className="capabilities-tagline">All from a single voice call inside the Tera app.</p>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <div className="section-divider">⸻</div>
          <h2>How Tera works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <p>Tap 🎙️ in the app-just like starting a phone call.</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <p>Speak naturally. Tera understands context, dates, clients, and amounts.</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <p>Get instant confirmation by voice. <br /> Task completed.</p>
              </div>
            </div>
          </div>
          <p className="privacy-note">Your data stays yours-encrypted, permission-based, and revocable at any time.</p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
        <div className="container">
          <div className="section-divider">⸻</div>
          <h2>Early Access Pricing</h2>
          <div className="pricing-amount-row">
            <span className="old-price">$32.99</span>
            <span className="highlight-price">$19.99</span>
            <span className="per-month">/ month</span>
          </div>
          <p className="pricing-intro">Join our waitlist to get early access, 14 day free trial + exclusive discounted pricing, and be the first to hear about new features and updates.</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="section-divider">⸻</div>
          <h2>Ready to reclaim your evenings?</h2>
          <p>Join the early-access waitlist and be first to drive with a real back-office co-pilot.</p>
          <WaitlistForm 
            className="cta-form" 
            email={email} 
            setEmail={setEmail} 
            isSubmitting={isSubmitting} 
            isSubmitted={isSubmitted} 
            error={error} 
            handleSubmit={handleSubmit} 
            buttonText="Get early access"
            source="bottom-cta"
          />
          <p className="built-by">Built by freelancers who were done with the 9 p.m. admin shift</p>
        </div>
      </section>
    </div>
  );
}

export default App;
